/**
 * AssetRadar — theme (day/night) context.
 *
 * Three states, not two: 'light', 'dark' and 'system'. 'system' is the default
 * and the only one that keeps following the OS, so a user who never touches the
 * switch gets the behaviour the stylesheet always had (a bare
 * `prefers-color-scheme` media query).
 *
 * How the choice reaches the CSS: the RESOLVED theme is stamped on
 * `<html data-theme="light|dark">`, and `styles/global.css` gives
 * `:root[data-theme="dark"]` the same token block as the media query while
 * guarding the query itself with `:root:not([data-theme="light"])`. That is what
 * makes an explicit choice win in BOTH directions — light on a dark OS included.
 *
 * Unlike the session (see lib/auth.tsx, which mirrors nothing), the preference
 * IS persisted in localStorage: it is a display setting, not an authorisation
 * fact, so a stale copy can at worst produce the wrong palette. The key is read
 * twice — once by the inline pre-paint script in index.html, which stamps
 * `data-theme` before React mounts so there is no white flash on a dark setup,
 * and once here. Keep `THEME_STORAGE_KEY` and that script in sync.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Also hard-coded in the pre-paint script in index.html — change both. */
export const THEME_STORAGE_KEY = 'craw_theme';

/** What the user picked. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** What is actually painted — 'system' resolved against the OS. */
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  /** The stored preference, including 'system'. */
  preference: ThemePreference;
  /** The palette on screen right now. */
  theme: ResolvedTheme;
  setPreference(preference: ThemePreference): void;
  /**
   * Flip the *visible* palette. From 'system' this lands on an explicit choice
   * opposite to what the OS currently says — which is what a user clicking a
   * sun/moon button means, rather than "cycle through three states".
   */
  toggle(): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** localStorage can throw (Safari private mode, disabled storage) — never fatal. */
function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // Follow the OS for as long as the preference is 'system'. The listener stays
  // attached regardless, so switching back to 'system' is immediately correct.
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setSystem(event.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', onChange);
    setSystem(media.matches ? 'dark' : 'light');
    return () => media.removeEventListener('change', onChange);
  }, []);

  const theme: ResolvedTheme = preference === 'system' ? system : preference;

  // The single place `data-theme` is written. `color-scheme` is set alongside it
  // so the browser's own chrome (scrollbars, form controls, the caret) follows
  // an explicit choice too — the stylesheet's `color-scheme: light dark` would
  // otherwise keep native widgets on the OS palette.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.style.colorScheme = theme;
  }, [theme]);

  const setPreference = useCallback((next: ThemePreference): void => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage unavailable: the choice still applies to this tab.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      theme,
      setPreference,
      toggle: () => setPreference(theme === 'dark' ? 'light' : 'dark'),
    }),
    [preference, theme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Access the theme context. Throws when used outside `ThemeProvider`. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme() muss innerhalb von <ThemeProvider> verwendet werden.');
  }
  return ctx;
}
